import { formatMoney } from "../../lib/format";
import type { Invoice } from "../../lib/gboModels";
import {
  customPaymentMinimum,
  validateCustomAmount,
} from "../../features/checkout/amountSelection";
import type { PaymentAmountKind } from "../../features/checkout/amountSelection";

type Props = {
  invoice?: Invoice;
  accountBalance: number;
  selected: PaymentAmountKind;
  customAmount: string;
  showCustomAmountError: boolean;
  onSelectedChange: (selected: PaymentAmountKind) => void;
  onCustomAmountChange: (amount: string) => void;
  onCustomAmountTouched: () => void;
};

export function PaymentAmountOptions({
  invoice,
  accountBalance,
  selected,
  customAmount,
  showCustomAmountError,
  onSelectedChange,
  onCustomAmountChange,
  onCustomAmountTouched,
}: Props) {
  const customValidation = validateCustomAmount(customAmount, accountBalance);

  return (
    <fieldset class="payment-options">
      <legend>Payment amount</legend>

      {invoice != null && invoice.balance > 0 && (
        <label class="payment-option">
          <input
            type="radio"
            name="payment-amount"
            value="invoice"
            checked={selected === "invoice"}
            onChange={() => onSelectedChange("invoice")}
          />
          <span>Invoice balance</span>
          <strong>{formatMoney(invoice.balance)}</strong>
        </label>
      )}

      {accountBalance > 0 && (
        <label class="payment-option">
          <input
            type="radio"
            name="payment-amount"
            value="account"
            checked={selected === "account"}
            onChange={() => onSelectedChange("account")}
          />
          <span>Account balance</span>
          <strong>{formatMoney(accountBalance)}</strong>
        </label>
      )}

      <label class="payment-option payment-option--custom">
        <input
          type="radio"
          name="payment-amount"
          value="custom"
          checked={selected === "custom"}
          onChange={() => onSelectedChange("custom")}
        />
        <span>Custom amount</span>
        <input
          aria-label="Custom payment amount"
          value={customAmount}
          onInput={(event) => onCustomAmountChange(event.currentTarget.value)}
          onFocus={() => onSelectedChange("custom")}
          onBlur={onCustomAmountTouched}
          inputMode="decimal"
          type="number"
          min={Math.min(customPaymentMinimum, accountBalance)}
          max={accountBalance}
          step="0.01"
          placeholder="0.00"
        />
      </label>

      {selected === "custom" && showCustomAmountError && customValidation && (
        <p class="payment-error" role="alert">
          {customValidation}
        </p>
      )}
    </fieldset>
  );
}
